import { Fragment, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  House,
  Person,
  Gear,
  Briefcase,
  FileEarmarkText,
  Search,
  ChevronLeft,
  ChevronRight,
  PersonFill,
  Envelope,
  GeoAlt,
} from "react-bootstrap-icons";
import "./Sidebar.css";

const navigation = [
  { name: "Dashboard", href: "/", icon: House },
  { name: "My Profile", href: "/profile", icon: Person },
  { name: "My Resume", href: "/resume", icon: FileEarmarkText },
  { name: "Saved Jobs", href: "/saved-jobs", icon: Briefcase },
  { name: "LinkedIn Search", href: "/linkedin-search", icon: Search },
  { name: "Settings", href: "/settings", icon: Gear },
];

interface SidebarProps {
  children: React.ReactNode;
  isCollapsed: boolean;
  toggleSidebar: () => void;
}

export default function Sidebar({
  children,
  isCollapsed,
  toggleSidebar,
}: SidebarProps) {
  const { logout } = useAuth() || {};
  const { currentUser } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="sidebar-container">
      {/* Sidebar */}
      <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header">
          <a href="/" className="sidebar-logo">
            {!isCollapsed && "LinkedIn Widget"}
          </a>
          <button
            className="sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight size={20} />
            ) : (
              <ChevronLeft size={20} />
            )}
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto">
          <div className="nav-links">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`nav-link ${isActive(item.href) ? "active" : ""}`}
                  title={isCollapsed ? item.name : ""}
                >
                  <Icon className="nav-icon" />
                  <span className="nav-text">{item.name}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="user-section p-4 border-t border-gray-200">
          <div className="flex items-center">
            {!isCollapsed && (
              <div className="ml-3">
                <div className="footer-user-details">
                  <PersonFill className="h-8 w-8 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-900">
                    {currentUser?.name || "User"}
                  </span>
                </div>

                <p className="text-xs text-gray-500">
                  {currentUser?.email || "user@example.com"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`main-content ${isCollapsed ? "expanded" : ""}`}>
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <h1 className="text-lg font-semibold text-gray-900">
              {navigation.find((item) => isActive(item.href))?.name ||
                "Dashboard"}
            </h1>
            <div className="flex items-center space-x-4">
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center space-x-2 focus:outline-none">
                  <span className="text-sm font-medium text-gray-700">
                    {currentUser?.name || "User"}
                  </span>
                  {/* <div className="flex-shrink-0">
                    <PersonFill className="h-8 w-8 text-gray-400" />
                  </div> */}
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/profile"
                            className={`${
                              active
                                ? "bg-gray-100 text-gray-900"
                                : "text-gray-700"
                            } block px-4 py-2 text-sm`}
                          >
                            Your Profile
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={logout}
                            className={`${
                              active
                                ? "bg-gray-100 text-gray-900"
                                : "text-gray-700"
                            } w-full text-left block px-4 py-2 text-sm`}
                          >
                            Sign out
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          {children}
        </main>
      </div>
    </div>
  );
}
